import React from 'react';
import {
    VStack,
    FormControl,
    FormLabel,
    Input,
    Button,
    Text,
    Box,
    Divider,
    HStack,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Select,
    Tag,
} from '@chakra-ui/react';
import { FiList } from 'react-icons/fi';
import {  } from 'react-icons/bs';

const STEP_TYPES = [
    { value: '',                    label: 'Generic Step',    color: 'purple' },
    { value: 'callFlowNode',        label: 'Call xflow',      color: 'teal'   },
    { value: 'callWhatsappFlowNode',label: 'WhatsApp Flow',   color: 'green'  },
    { value: 'ivrNode',             label: 'IVR Call',        color: 'cyan'   },
]

function StepTypeTag({ value }) {
    const t = STEP_TYPES.find(s => s.value === value) || STEP_TYPES[0]
    return (
        <Tag colorScheme={t.color} variant="subtle" fontSize="10px" px={1.5}>
            {t.label}
        </Tag>
    )
}

export default function MultiStepProperties({ nodeConfig, setNodeConfig, updateNodeData, setSelectedNode }) {
    const stepCount = nodeConfig.stepCount || 3;
    const stepNames = nodeConfig.stepNames || Array.from({ length: stepCount }, (_, i) => `Step ${i + 1}`);
    const stepTypes = nodeConfig.stepTypes || Array(stepCount).fill('');

    const handleStepCountChange = (valueStr) => {
        const newCount = Math.max(2, Math.min(10, parseInt(valueStr) || 2));
        const newNames = [...stepNames];
        const newTypes = [...stepTypes];

        if (newCount > newNames.length) {
            for (let i = newNames.length; i < newCount; i++) {
                newNames.push(`Step ${i + 1}`);
                newTypes.push('');
            }
        } else {
            newNames.splice(newCount);
            newTypes.splice(newCount);
        }

        setNodeConfig({ ...nodeConfig, stepCount: newCount, stepNames: newNames, stepTypes: newTypes });
    };

    const handleStepNameChange = (index, value) => {
        const newNames = [...stepNames];
        newNames[index] = value;
        setNodeConfig({ ...nodeConfig, stepNames: newNames });
    };

    const handleStepTypeChange = (index, value) => {
        const newTypes = [...stepTypes];
        newTypes[index] = value;
        setNodeConfig({ ...nodeConfig, stepTypes: newTypes });
    };

    return (
        <VStack spacing={4} align="stretch">
            <FormControl>
                <FormLabel fontSize="sm">Step Name</FormLabel>
                <Input
                    size="sm"
                    value={nodeConfig.label || ''}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                    placeholder="e.g., Multi Step Flow"
                />
            </FormControl>

            <Divider />

            <FormControl>
                <FormLabel fontSize="sm">Number of Steps</FormLabel>
                <NumberInput
                    size="sm"
                    min={2}
                    max={10}
                    value={stepCount}
                    onChange={handleStepCountChange}
                >
                    <NumberInputField />
                    <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                    </NumberInputStepper>
                </NumberInput>
                <Text fontSize="xs" color="gray.500" mt={1}>
                    Configure between 2 and 10 steps
                </Text>
            </FormControl>

            <Divider />

            <FormControl>
                <FormLabel fontSize="sm" mb={3}>Step Configuration</FormLabel>
                <VStack spacing={3} align="stretch" maxH="340px" overflowY="auto">
                    {Array.from({ length: stepCount }, (_, i) => (
                        <Box
                            key={i}
                            p={2.5}
                            bg="purple.50"
                            border="1px solid"
                            borderColor="purple.200"
                            borderRadius="md"
                        >
                            <HStack spacing={2} mb={2}>
                                <Text fontSize="xs" fontWeight="semibold" color="purple.600" minW="20px">
                                    {i + 1}.
                                </Text>
                                <Input
                                    size="sm"
                                    value={stepNames[i] || `Step ${i + 1}`}
                                    onChange={(e) => handleStepNameChange(i, e.target.value)}
                                    placeholder={`Step ${i + 1}`}
                                    bg="white"
                                />
                            </HStack>
                            <HStack spacing={2}>
                                <Text fontSize="xs" color="gray.500" minW="36px">Type:</Text>
                                <Select
                                    size="xs"
                                    value={stepTypes[i] || ''}
                                    onChange={(e) => handleStepTypeChange(i, e.target.value)}
                                    bg="white"
                                    borderRadius="md"
                                >
                                    {STEP_TYPES.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </Select>
                                <StepTypeTag value={stepTypes[i] || ''} />
                            </HStack>
                        </Box>
                    ))}
                </VStack>
            </FormControl>

            <Divider />

            <Box p={3} bg="purple.50" border="1px" borderColor="purple.200" borderRadius="md">
                <HStack spacing={2} align="flex-start">
                    <Box color="purple.600" mt={0.5} flexShrink={0}>
                        <FiList size={14} />
                    </Box>
                    <Box>
                        <Text fontSize="xs" fontWeight="medium" color="purple.700" mb={1}>
                            Multi Step Node
                        </Text>
                        <VStack spacing={0.5} align="stretch">
                            <Text fontSize="xs" color="purple.600">• Creates multiple sequential output paths</Text>
                            <Text fontSize="xs" color="purple.600">• Set step type: Call xflow or WhatsApp Flow</Text>
                            <Text fontSize="xs" color="purple.600">• Each step connects to its own flow node</Text>
                        </VStack>
                    </Box>
                </HStack>
            </Box>

            <HStack>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedNode && setSelectedNode(null)}
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    bg="purple.600"
                    color="white"
                    _hover={{ bg: 'purple.700' }}
                    onClick={updateNodeData}
                    flex={1}
                >
                    Update Node
                </Button>
            </HStack>
        </VStack>
    );
}
